import { LitElement, html } from 'lit';

import { navigate } from '../../lib/router/index.js';
import { ACTION_LABEL, revisionAction } from '../actions.js';
import { api } from '../api/index.js';
import { confirmAction } from '../components/plane-confirm.js';
import { revisionStatusBadge, stateBadge } from '../components/plane-badge.js';
import { loadState, staleWarning } from '../components/load-state.js';
import { when } from '../components/time.js';
import { revisionLabel, shortDigest } from '../format.js';
import { HEALTH, stateHint, stateOf } from '../i18n/labels.js';
import { poll } from '../poll.js';
import { currentViewer } from '../viewer.js';
import { formStyles } from '../../styles/shared/forms.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { instancePageStyles } from '../../styles/pages/instance-page.js';

/**
 * One instance: the desired and the applied revision side by side, the revisions with Apply and
 * Rollback, the secrets, and the phases of the latest applies.
 *
 * Apply never shows an outcome: the click changes the desired state, and the page shows
 * "Updating" until the server reports (plane/README.md: "Nessun «Applicato!» dopo il click").
 */
/** No desired revision and none ever published: the server never ran it, so it can be deleted. */
const neverDeployed = (instance) => !instance.desiredRevision && !instance.revisions.some((revision) => revision.status === 'published');

class InstancePage extends LitElement {
  static properties = {
    name: { type: String },
    busy: { state: true },
    actionError: { state: true },
    editingSecret: { state: true },
    customerOptions: { state: true },
    renaming: { state: true },
    adding: { state: true },
  };

  static styles = [
    pageStyles,
    tableStyles,
    formStyles,
    instancePageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    this.busy = false;
    this.actionError = null;
    this.editingSecret = null;
    this.customerOptions = null;
    this.poll = poll(this, async () => {
      const [instance, viewer] = await Promise.all([api.instance(this.name), currentViewer()]);
      if (viewer.operator && this.customerOptions === null) {
        // the customers an operator can assign the instance to: loaded once, they change rarely
        this.customerOptions = await api.customers();
      }
      return { instance, viewer };
    });
  }

  updated(changed) {
    if (changed.has('name') && changed.get('name') !== undefined) {
      this.actionError = null;
      this.poll.reset();
    }
  }

  /** Runs an action, then reloads: what the page shows next is the reported state. */
  async act(action) {
    this.busy = true;
    this.actionError = null;
    try {
      await action();
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  async apply(revision, kind) {
    if (
      kind === 'rollback' &&
      !(await confirmAction({
        heading: `Roll back ${this.name}?`,
        message: `It goes back to revision ${revision.number}.`,
        confirmLabel: 'Roll back',
      }))
    ) {
      return;
    }
    this.act(() => api.apply(this.name, revision.id));
  }

  async restart() {
    if (!(await confirmAction({ heading: `Restart the workspace ${this.poll.value.instance.displayName}?`, message: 'The agent is unavailable for a few seconds.', confirmLabel: 'Restart', tone: 'danger' }))) {
      return;
    }
    this.act(() => api.restart(this.name));
  }

  /** Renames the workspace; the name of the instance (the gateway) stays. The form stays open when the name is refused. */
  /** Opens the form of "Add assistant", with the templates in use to choose from. */
  async startAdding() {
    this.actionError = null;
    try {
      this.templateOptions = await api.templates();
      this.adding = true;
    } catch (error) {
      this.actionError = error.message;
    }
  }

  /** One more assistant from a template, applied at once: the form stays open when it is refused. */
  async addAssistant(event) {
    event.preventDefault();
    const form = event.target.elements;
    this.busy = true;
    this.actionError = null;
    try {
      await api.addAssistantFromTemplate(this.name, { templateId: form.template.value, assistantId: form.assistantId.value.trim() || null });
      this.adding = false;
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  renderAddAssistant(instance) {
    const options = (this.templateOptions ?? []).filter((template) => template.imageVersion === instance.imageVersion);
    if (options.length === 0) {
      return html`<p class="muted">No template in use for image <code>${instance.imageVersion}</code>: <a href="/templates/new">make one</a> first.</p>`;
    }
    return html`
      <form class="inline" @submit=${(event) => this.addAssistant(event)}>
        <select
          name="template"
          aria-label="Template"
          ?disabled=${this.busy}
          @change=${(event) => {
            event.target.form.elements.assistantId.value = options.find((template) => template.id === event.target.value)?.assistantId ?? '';
          }}
        >
          ${options.map((template) => html`<option value=${template.id}>${template.name}</option>`)}
        </select>
        <input name="assistantId" type="text" pattern="[a-z0-9]([a-z0-9\\-]{0,30}[a-z0-9])?" maxlength="32" aria-label="Assistant id" .value=${options[0].assistantId} ?disabled=${this.busy} />
        <button class="primary" ?disabled=${this.busy}>Add and apply</button>
        <button type="button" ?disabled=${this.busy} @click=${() => (this.adding = false)}>Cancel</button>
      </form>
      <p class="muted">
        A new revision with one more assistant, the template's, applied at once; its id never changes. The other assistants and the operator rules stay as they are: the rules are
        those of the template the workspace was created from.
      </p>
    `;
  }

  async rename(event) {
    event.preventDefault();
    this.busy = true;
    this.actionError = null;
    try {
      await api.renameWorkspace(this.name, event.target.elements['workspace-name'].value);
      this.renaming = false;
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  /** Operators only: the instance leaves its server; its revisions stay, and Apply deploys it again. */
  async removeFromServer() {
    if (
      !(await confirmAction({
        heading: `Remove the workspace ${this.poll.value.instance.displayName} from server ${this.poll.value.instance.serverId}?`,
        message: 'The agent stops; its state volume stays on the server, and applying a revision deploys it again.',
        confirmLabel: 'Remove from server',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.act(() => api.removeInstance(this.name));
  }

  /** Operators only: an instance never deployed goes away with its drafts, and its name is free again. */
  async deleteInstance() {
    if (
      !(await confirmAction({
        heading: `Delete the workspace ${this.poll.value.instance.displayName}?`,
        message: 'It was never deployed: the workspace and its drafts are deleted, and its id can be used again.',
        confirmLabel: 'Delete',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.busy = true;
    this.actionError = null;
    try {
      await api.deleteInstance(this.name);
      navigate('/workspaces');
    } catch (error) {
      this.actionError = error.message;
      this.poll.refresh();
    } finally {
      this.busy = false;
    }
  }

  async saveSecret(event, secretName) {
    event.preventDefault();
    const input = event.target.elements.value;
    const value = input.value;
    await this.act(async () => {
      await api.setSecret(this.name, secretName, value);
      this.editingSecret = null;
    });
    input.value = '';
  }

  /**
   * The state of the channel accounts of the instance, as its clawlet last read it from the gateway:
   * nothing when the server does not report channels (an older clawlet) or none runs.
   */
  renderChannels(instance) {
    const channels = instance.channels ?? [];
    if (channels.length === 0) {
      return null;
    }
    return html`
      <section class="revisions">
        <h2>Channels</h2>
        <table>
          <thead>
            <tr>
              <th>Assistant</th>
              <th>Channel</th>
              <th>State</th>
              <th>Reported</th>
            </tr>
          </thead>
          <tbody>
            ${channels.map(
              (channel) => html`
                <tr>
                  <td><code>${channel.assistant}</code></td>
                  <td>${channel.channel}</td>
                  <td>${channel.state}${channel.error ? html`<div class="muted">${channel.error}</div>` : null}</td>
                  <td>${when(channel.reportedAt)}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </section>
    `;
  }

  /**
   * Who wrote to the bot of an assistant in pairing and waits: Allow adds them to its allowed users
   * in a new revision applied at once; Ignore hides the request until it expires.
   */
  renderPairingRequests(instance) {
    const requests = instance.pairingRequests ?? [];
    if (requests.length === 0) {
      return null;
    }
    return html`
      <section class="revisions">
        <h2>Waiting to talk to an assistant</h2>
        <table>
          <thead>
            <tr>
              <th>Assistant</th>
              <th>Channel</th>
              <th>Who</th>
              <th>Code</th>
              <th>Since</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${requests.map(
              (request) => html`
                <tr>
                  <td><code>${request.assistant}</code></td>
                  <td>${request.channel ?? 'telegram'}</td>
                  <td>
                    ${request.name ?? ''} ${request.username ? html`<span class="muted">@${request.username}</span>` : null}
                    <div><code>${request.senderId}</code></div>
                  </td>
                  <td><code>${request.code}</code></td>
                  <td>${when(request.requestedAt)}</td>
                  <td>
                    <button class="small primary" ?disabled=${this.busy}
                      @click=${() => this.act(() => api.allowPairingRequest(this.name, request.assistant, request.channel ?? 'telegram', request.senderId))}>Allow</button>
                    <button class="small" ?disabled=${this.busy}
                      @click=${() => this.act(() => api.ignorePairingRequest(this.name, request.assistant, request.channel ?? 'telegram', request.senderId))}>Ignore</button>
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </section>
    `;
  }

  /** Operators only: who the instance belongs to, and so which customer users see it. */
  /** The actions used less often than Restart and Edit: in the "⋯" menu of the head. */
  renderMoreActions(instance, viewer) {
    const items = [
      instance.canManage && !this.renaming ? html`<button ?disabled=${this.busy} @click=${() => (this.renaming = true)}>Rename</button>` : null,
      viewer.operator && !this.adding ? html`<button ?disabled=${this.busy} @click=${() => this.startAdding()}>Add assistant</button>` : null,
      viewer.operator && instance.desiredRevision
        ? html`<a class="button" href="/templates/new?fromInstance=${encodeURIComponent(instance.ref ?? instance.name)}">Make a template</a>`
        : null,
    ].filter(Boolean);
    return items.length ? html`<plane-menu label="More actions">${items}</plane-menu>` : null;
  }

  /** Operators only: who the workspace is for, and the actions that take it away from its server. */
  renderSettings(instance) {
    const removable = Boolean(instance.desiredRevision);
    const deletable = neverDeployed(instance);
    return html`
      <section class="settings">
        <h2>Settings</h2>
        <div class="card">
          <div class="field">
            <label for="customer">Customer</label>
            <span class="help">Its members see the workspace; changed at once.</span>
            ${this.renderCustomerChoice(instance)}
          </div>
        </div>
        ${removable || deletable
          ? html`
              <div class="card danger-zone">
                <h3>Danger zone</h3>
                ${removable
                  ? html`<div class="danger-action">
                      <p><strong>Remove from server</strong> The agent stops; its state volume stays on the server, and applying a revision deploys it again.</p>
                      <button class="danger" ?disabled=${this.busy} @click=${() => this.removeFromServer()}>Remove from server</button>
                    </div>`
                  : null}
                ${deletable
                  ? html`<div class="danger-action">
                      <p><strong>Delete</strong> It was never deployed: the workspace and its drafts go away, and its id can be used again.</p>
                      <button class="danger" ?disabled=${this.busy} @click=${() => this.deleteInstance()}>Delete</button>
                    </div>`
                  : null}
              </div>
            `
          : null}
      </section>
    `;
  }

  renderCustomerChoice(instance) {
    const options = this.customerOptions ?? [];
    return html`
      <select
        id="customer"
        class="customer-choice"
        ?disabled=${this.busy}
        @change=${(event) => this.act(() => api.assignCustomer(this.name, event.target.value || null))}
      >
        <option value="" ?selected=${!instance.customer}>No customer</option>
        ${options.map((customer) => html`<option value=${customer.id} ?selected=${customer.name === instance.customer}>${customer.name}</option>`)}
      </select>
    `;
  }

  renderState(instance) {
    const state = stateOf(instance.state);
    if (instance.state === 'running') {
      return null;
    }
    return html`
      <div class="notice" data-tone=${state.tone} role="status">
        <strong>${state.label}</strong>
        ${stateHint(instance)} ${instance.error ? html`<div><code>${instance.error}</code></div>` : null}
      </div>
    `;
  }

  renderSides(instance) {
    const { desiredRevision: desired, appliedRevision: applied } = instance;
    const inSync = desired?.id === applied?.id;
    const revisionLink = (ref) =>
      ref ? html`<div class="number"><a href="/workspaces/${this.name}/revisions/${ref.id}">Revision ${revisionLabel(ref).replace('#', '')}</a></div>` : html`<div class="number muted">None</div>`;
    return html`
      <div class="sides">
        <div class="card side">
          <h3>Desired</h3>
          ${revisionLink(desired)}
          <dl class="facts">
            <dt>Acknowledged</dt>
            <dd>${when(instance.desiredAckedAt, 'not yet')}</dd>
            <dt>Restarts asked</dt>
            <dd>${instance.restartNonce}</dd>
          </dl>
        </div>
        <div class="between" data-sync=${inSync} aria-hidden="true">${inSync ? '=' : '→'}</div>
        <div class="card side">
          <h3>Applied, as reported</h3>
          ${revisionLink(applied)}
          <dl class="facts">
            <dt>State</dt>
            <dd>${stateBadge(instance.state)}</dd>
            <dt>Health</dt>
            <dd>${HEALTH[instance.health] ?? '—'}</dd>
            <dt>Since</dt>
            <dd>${when(instance.since)}</dd>
          </dl>
        </div>
      </div>
    `;
  }

  renderRevisions(instance) {
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Revision</th>
              <th>Status</th>
              <th>Author</th>
              <th>Created</th>
              <th>Digest</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${instance.revisions.map((revision) => {
              const action = revisionAction(revision, instance);
              const markers = [
                revision.id === instance.desiredRevision?.id ? 'desired' : null,
                revision.id === instance.appliedRevision?.id ? 'applied' : null,
              ].filter(Boolean);
              return html`
                <tr class=${markers.includes('desired') ? 'current' : ''}>
                  <td>
                    <a href="/workspaces/${this.name}/revisions/${revision.id}">#${revision.number}</a>
                    ${markers.map((marker) => html`<span class="marker">${marker}</span>`)}
                  </td>
                  <td>${revisionStatusBadge(revision.status)}</td>
                  <td>${revision.author}</td>
                  <td>${when(revision.createdAt)}</td>
                  <td><code title=${revision.digest ?? ''}>${shortDigest(revision.digest) || '—'}</code></td>
                  <td class="actions-cell">
                    <a class="button small" href="/workspaces/${this.name}/edit?from=${revision.id}">Edit from here</a>
                    ${action
                      ? html`<button class="small ${action === 'apply' ? 'primary' : ''}" ?disabled=${this.busy} @click=${() => this.apply(revision, action)}>
                          ${ACTION_LABEL[action]}
                        </button>`
                      : null}
                  </td>
                </tr>
              `;
            })}
          </tbody>
        </table>
      </div>
    `;
  }

  /** Where a secret is: never its value, which the plane cannot read once sealed. */
  renderSecretState(secret) {
    if (!secret.set) {
      return html`<plane-badge tone="muted">Not set</plane-badge>`;
    }
    if (secret.rejection) {
      return html`<plane-badge tone="danger" title=${secret.rejection}>Rejected by the server</plane-badge>`;
    }
    if (!secret.deliveredAt) {
      return html`<plane-badge tone="progress" pulse title="Sealed and sent: waiting for the server to report it">On its way</plane-badge>`;
    }
    return html`<plane-badge tone="ok" title="The server reported holding this version">Delivered</plane-badge>`;
  }

  renderSecrets(instance) {
    if (instance.secrets.length === 0) {
      return html`<p class="empty">The image of this workspace uses no secret.</p>`;
    }
    return html`
      <div class="table-wrap">
        <table>
          <tbody>
            ${instance.secrets.map(
              (secret) => html`
                <tr class=${this.editingSecret === secret.name ? 'editing' : ''}>
                  <td class="wrap">
                    <strong>${secret.title}</strong>
                    <div><code>${secret.name}</code></div>
                  </td>
                  <td class="wrap">
                    ${this.renderSecretState(secret)}
                    ${secret.set
                      ? html`<div class="muted">Version ${secret.version}, set ${when(secret.updatedAt)}${secret.setBy ? ` by ${secret.setBy}` : ''}</div>`
                      : null}
                    ${secret.rejection ? html`<div class="muted">${secret.rejection}: set it again.</div>` : null}
                  </td>
                  <td class="actions-cell">
                    ${this.editingSecret === secret.name
                      ? null
                      : html`<button class="small" @click=${() => (this.editingSecret = secret.name)}>${secret.set ? 'Replace' : 'Set'}</button>`}
                  </td>
                </tr>
                ${this.editingSecret === secret.name
                  ? html`
                      <tr>
                        <td colspan="3">
                          <form class="secret-form" @submit=${(event) => this.saveSecret(event, secret.name)}>
                            <input name="value" type="password" autocomplete="off" required aria-label="New value of ${secret.name}" placeholder="New value" />
                            <button class="small primary" ?disabled=${this.busy}>Save</button>
                            <button class="small" type="button" @click=${() => (this.editingSecret = null)}>Cancel</button>
                          </form>
                        </td>
                      </tr>
                    `
                  : null}
              `,
            )}
          </tbody>
        </table>
      </div>
      <p class="muted">
        The value reaches the plane over HTTPS, is sealed there at once to the key of the server and stored only in
        that form: the plane cannot read it back. The server applies it as soon as it receives it.
      </p>
    `;
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/workspaces">Workspaces</a> / ${this.poll.value?.instance.displayName ?? this.name}</nav>`;
    const pending = loadState(this.poll, 'the workspace');
    if (pending) {
      return html`${crumbs}<h1><code>${this.name}</code></h1>${pending}`;
    }
    const { instance, viewer } = this.poll.value;
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>${instance.displayName} ${stateBadge(instance.state)}</h1>
          <p class="lead">
            id <code>${instance.name}</code> · ${instance.customer ?? html`<span class="muted">no customer</span>`} · server
            ${viewer.operator ? html`<a href="/servers/${instance.serverId}"><code>${instance.serverId}</code></a>` : html`<code>${instance.serverId}</code>`}
            · image <code>${instance.imageVersion}</code>
          </p>
        </div>
        <div class="actions">
          ${this.renderMoreActions(instance, viewer)}
          ${instance.desiredRevision ? html`<button ?disabled=${this.busy} @click=${() => this.restart()}>Restart</button>` : null}
          <a class="button primary" href="/workspaces/${this.name}/edit">Edit configuration</a>
        </div>
      </header>
      ${this.renaming
        ? html`<form class="inline" @submit=${(event) => this.rename(event)}>
            <input name="workspace-name" type="text" required maxlength="60" aria-label="Name of the workspace" .value=${instance.displayName} ?disabled=${this.busy} />
            <button class="primary" ?disabled=${this.busy}>Save</button>
            <button type="button" ?disabled=${this.busy} @click=${() => (this.renaming = false)}>Cancel</button>
          </form>`
        : null}
      ${this.adding ? this.renderAddAssistant(instance) : null}
      ${staleWarning(this.poll)}
      ${this.actionError ? html`<div class="notice" data-tone="danger" role="alert"><strong>The action failed</strong>${this.actionError}</div>` : null}
      ${this.renderState(instance)} ${this.renderSides(instance)} ${this.renderPairingRequests(instance)} ${this.renderChannels(instance)}
      <section class="revisions">
        <h2>Revisions</h2>
        ${this.renderRevisions(instance)}
      </section>
      <div class="columns">
        <section>
          <h2>Apply events</h2>
          <plane-event-list .events=${instance.events}></plane-event-list>
        </section>
        <section>
          <h2>Secrets</h2>
          ${this.renderSecrets(instance)}
        </section>
      </div>
      ${viewer.operator ? this.renderSettings(instance) : null}
    `;
  }
}

customElements.define('plane-instance-page', InstancePage);
